/**
 * WHAT: Displays data classification types and sensitivity levels in a tabbed view.
 * WHY: Classification and sensitivity metadata drive PRO scope rules and attestation
 *      frequency; this read-only view lets admins verify the current taxonomy.
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_CONFIG from '../../config/api.config';

/**
 * WHAT: Tabbed component showing classification types table and sensitivity level cards.
 * WHY: Admins need to review and verify the classification taxonomy that governs
 *      data sensitivity scores and attestation frequency requirements.
 */
const ProClassifications = () => {
    const [classificationTypes, setClassificationTypes] = useState([]);
    const [sensitivityLevels, setSensitivityLevels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeSection, setActiveSection] = useState('classifications');

    useEffect(() => {
        fetchData();
    }, []);

    /* WHAT: Fetches classification types and sensitivity levels in parallel.
     * WHY: Both datasets are needed on initial load; parallel requests reduce
     *      page load time compared to sequential fetching. */
    const fetchData = async () => {
        setLoading(true);
        try {
            const [classRes, sensRes] = await Promise.all([
                axios.get(`${API_CONFIG.BASE_URL}/api/pro/classification-types/`),
                axios.get(`${API_CONFIG.BASE_URL}/api/pro/sensitivity-levels/`)
            ]);
            setClassificationTypes(classRes.data.classification_types || []);
            setSensitivityLevels(sensRes.data.sensitivity_levels || []);
        } catch (err) {
            console.error('Error fetching data:', err);
        } finally {
            setLoading(false);
        }
    };

    /* WHAT: Maps a classification category to its Bootstrap badge color class.
     * WHY: Color-coding (red=REGULATORY, blue=BUSINESS, yellow=SECURITY) helps
     *      admins quickly distinguish classification categories in the table. */
    const getCategoryBadge = (category) => {
        const badges = {
            'REGULATORY': 'bg-danger',
            'BUSINESS': 'bg-primary',
            'SECURITY': 'bg-warning text-dark'
        };
        return badges[category] || 'bg-secondary';
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">Classifications & Sensitivity</h4>
                    <p className="text-muted mb-0">
                        Data classification types and sensitivity levels
                    </p>
                </div>
            </div>

            {/* Section Tabs */}
            <ul className="nav nav-pills mb-4">
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeSection === 'classifications' ? 'active' : ''}`}
                        onClick={() => setActiveSection('classifications')}
                    >
                        <i className="fas fa-tags me-2"></i>
                        Classification Types ({classificationTypes.length})
                    </button>
                </li>
                <li className="nav-item">
                    <button
                        className={`nav-link ${activeSection === 'sensitivity' ? 'active' : ''}`}
                        onClick={() => setActiveSection('sensitivity')}
                    >
                        <i className="fas fa-shield-alt me-2"></i>
                        Sensitivity Levels ({sensitivityLevels.length})
                    </button>
                </li>
            </ul>

            {activeSection === 'classifications' && (
                <div className="card">
                    <div className="card-header bg-secondary text-white">
                        <h5 className="mb-0">
                            <i className="fas fa-tags me-2"></i>
                            Classification Types
                        </h5>
                    </div>
                    <div className="card-body p-0">
                        <table className="table table-hover mb-0">
                            <thead className="table-dark">
                                <tr>
                                    <th>Code</th>
                                    <th>Name</th>
                                    <th>Category</th>
                                    <th>Default Sensitivity</th>
                                    <th>Regulatory Framework</th>
                                    <th>System</th>
                                </tr>
                            </thead>
                            <tbody>
                                {classificationTypes.map(ct => (
                                    <tr key={ct.classification_id}>
                                        <td>
                                            <span
                                                className="badge"
                                                style={{ backgroundColor: ct.color || '#6c757d' }}
                                            >
                                                {ct.classification_code}
                                            </span>
                                        </td>
                                        <td>
                                            <strong>{ct.classification_name}</strong>
                                            {ct.description && (
                                                <div className="small text-muted">{ct.description}</div>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`badge ${getCategoryBadge(ct.category)}`}>
                                                {ct.category}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`badge ${
                                                ct.default_sensitivity_level === 'CRITICAL' ? 'bg-danger' :
                                                ct.default_sensitivity_level === 'HIGH' ? 'bg-warning text-dark' :
                                                ct.default_sensitivity_level === 'MEDIUM' ? 'bg-info' : 'bg-success'
                                            }`}>
                                                {ct.default_sensitivity_level}
                                            </span>
                                        </td>
                                        <td>
                                            {ct.regulatory_framework ? (
                                                <span className="badge bg-secondary">{ct.regulatory_framework}</span>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            {ct.is_system ? (
                                                <i className="fas fa-check text-success"></i>
                                            ) : (
                                                <i className="fas fa-user text-muted"></i>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {activeSection === 'sensitivity' && (
                <div className="row">
                    {sensitivityLevels.map(level => (
                        <div key={level.level_id} className="col-md-6 col-lg-3 mb-4">
                            <div className="card h-100" style={{ borderLeft: `4px solid ${level.color}` }}>
                                <div className="card-body">
                                    <div className="d-flex justify-content-between align-items-start mb-3">
                                        <h5 className="card-title mb-0">
                                            <span
                                                className="badge me-2"
                                                style={{ backgroundColor: level.color }}
                                            >
                                                {level.level_code}
                                            </span>
                                        </h5>
                                        <span className="badge bg-secondary">
                                            Rank: {level.rank_order}
                                        </span>
                                    </div>
                                    <h6 className="card-subtitle text-muted mb-3">
                                        {level.level_name}
                                    </h6>
                                    <p className="card-text small">
                                        {level.description}
                                    </p>
                                    <hr />
                                    <div className="small">
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>Attestation Frequency:</span>
                                            <strong>
                                                {level.attestation_frequency_days ? `${level.attestation_frequency_days} days` : 'N/A'}
                                            </strong>
                                        </div>
                                        <div className="d-flex justify-content-between mb-2">
                                            <span>Manager Approval:</span>
                                            {level.requires_manager_approval ? (
                                                <i className="fas fa-check text-success"></i>
                                            ) : (
                                                <i className="fas fa-times text-muted"></i>
                                            )}
                                        </div>
                                        <div className="d-flex justify-content-between">
                                            <span>Data Owner Approval:</span>
                                            {level.requires_data_owner_approval ? (
                                                <i className="fas fa-check text-success"></i>
                                            ) : (
                                                <i className="fas fa-times text-muted"></i>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProClassifications;
